import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { AdAuthGuard, authSelectors, AuthStrategy, Auth } from '@content-platform/web-angular-shared-util-auth';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { userPermissionListActions } from '../actions';
import { ApplicationApiConstants } from '../application-api-config';
import { fromUserPermissionList } from '../reducers';
import { userPermissionListSelectors } from '../selectors';

const directAccessApps = ['Order Management Console', 'C3 Linear Monitoring'];


@Injectable()
export class UserPermissionListGuard implements CanActivate {
  constructor(
    protected store: Store<fromUserPermissionList.State>,
    protected authStore: Store<Auth>,
    protected router: Router,
    private authGuard: AdAuthGuard) {}

  canActivate(): Observable<boolean> {
    return this.checkLoggedIn().pipe(
      switchMap((loggedIn) => {
        if (!loggedIn) {
          return of(false);
        }
        const permissionSub =  this.checkStoreAndWaitForLoad().pipe(
          switchMap((loadedPermissions) => {
            if(directAccessApps.includes(ApplicationApiConstants.activeAppName)){
              return of(true);
            }
            if (loadedPermissions) {
              return this.checkIfUserHasPermissions().pipe(
                tap((hasPermission) => {
                  if (!hasPermission) {
                    if (
                      ApplicationApiConstants.activeAppName === 'Registration & Enrichment Requests'
                    ) {
                      this.router.navigate(['/missing-group'], { skipLocationChange: true });
                    } else {
                      this.router.navigate(['/access-denied'], { skipLocationChange: true });
                    }
                  }
                })
              );
            }
            return of(loadedPermissions);
          }),
          catchError(() => 
            ApplicationApiConstants.activeAppName === 'Order Management Console' ? of(true) : of(false)
          )
        );
        if(directAccessApps.includes(ApplicationApiConstants.activeAppName)){
          permissionSub.pipe(take(1)).subscribe();
          return of(true);
        }
        else{
          return permissionSub;
        }
      })
    );
  }

  checkLoggedIn(): Observable<boolean> {
    return this.authStore.select(authSelectors.getAuthenticationStrategy).pipe(
      filter(strategy => !!strategy),
      switchMap((authStrategy: AuthStrategy) => authStrategy === AuthStrategy.OKTA ?
        this.store.pipe(select(authSelectors.getADUser))
          .pipe(map(user => !!user))
        : this.authGuard.canActivate()));
  }

  /**
   * Checks to see if a user permission list has been loaded
   * Side effect: if it hasn't, wait for it to load if possible
   *
   * @returns {Observable<boolean>}
   * @memberof UserPermissionListGuard
   */
  checkStoreAndWaitForLoad(): Observable<boolean> {
    let triedToLoadPermissionList = false;

    return this.store.pipe(select(userPermissionListSelectors.getUserPermissionListLoaded)).pipe(
      withLatestFrom(
        this.store.pipe(select(userPermissionListSelectors.getUserPermissionListLoading)),
        this.store.pipe(select(authSelectors.getADUser)),
        this.store.pipe(select(authSelectors.getIsSettingUser)),
        this.store.pipe(select(authSelectors.getLoginFailed))
      ),
      filter(([loaded, loading, user, settingUser, loginFailed]) => {
        // If login failed, continue
        if (loginFailed) {
          return true;
        }
        // If the permission list hasn't loaded yet, nothing is currently loading, we haven't tried to load the list yet
        // but we have a valid user, try loading the permission list (this should set loading to true on subsequent runs)
        // Wait for some state change before continuing
        if (!loaded && !loading && !triedToLoadPermissionList && user) {
          this.store.dispatch(new userPermissionListActions.LoadById(user.profile.oid));
          triedToLoadPermissionList = true;
          return false;
        }

        // If we aren't loading anything, and we're also not trying to set the user continue
        // In this case, either:
        // the list is already loaded (good),
        // or we haven't tried to load the list and we'll never have a valid user in order to do so
        return !loading && !settingUser;
      }),
      map(([loaded]) => loaded),
      take(1)
    );
  }

  checkIfUserHasPermissions(): Observable<boolean> {
    return this.store.pipe(select(userPermissionListSelectors.hasUserPermission));
  }
}
